/**
 * Selectors verified against the live Hae Hae theme (haehae.co, theme
 * "Hae Hae — Bundle Checkout Link 2026-08-17", fetched via the Shopify
 * Admin GraphQL `theme.files` API on 2026-08-17) — NOT guessed. Doc
 * section 24: "Before adding selectors, inspect the actual rendered
 * storefront. Do not guess CSS selectors."
 *
 * Source files inspected: snippets/buy-buttons.liquid,
 * snippets/product-variant-picker.liquid, snippets/product-variants.liquid,
 * snippets/product-complementary.liquid, sections/hh-bundle-widget.liquid,
 * sections/main-cart.liquid, sections/product-details.liquid,
 * snippets/header-drawer.liquid.
 *
 * These are used as PRIORITY-2 "stable Shopify/app identifier" matches in
 * elementKey.ts — priority 1 (explicit data-hh-* attributes) always wins if
 * present, and anything not matched here still falls through to the
 * generic CSS-selector-generation heuristic. If the theme changes, this
 * file degrades gracefully rather than breaking: unmatched elements just
 * get a slightly less human-readable element_key.
 */

export interface KnownSelector {
  /** Human-readable element_key emitted when this selector matches. */
  key: string;
  /** CSS selector (or an element-matches test — see matches()) used to identify it. */
  selector: string;
  /** doc section 5's specific areas of interest this maps to. */
  category:
    | "add_to_cart"
    | "variant_picker"
    | "size_picker"
    | "quantity"
    | "bundle"
    | "complementary_products"
    | "cart_drawer"
    | "checkout"
    | "accordion"
    | "reviews"
    | "shipping_info";
}

// Ordered — first match wins (most specific first).
export const KNOWN_SELECTORS: KnownSelector[] = [
  // --- Add to cart / product form (snippets/buy-buttons.liquid) ---
  { key: "add_to_cart_button", selector: 'form[data-type="add-to-cart-form"] button[type="submit"], form[data-type="add-to-cart-form"] button[name="add"]', category: "add_to_cart" },
  { key: "quantity_increment", selector: 'quantity-input button[name="plus"]', category: "quantity" },
  { key: "quantity_decrement", selector: 'quantity-input button[name="minus"]', category: "quantity" },
  { key: "quantity_input", selector: "quantity-input input.quantity__input", category: "quantity" },

  // --- Variant / size picker (snippets/product-variant-picker.liquid) ---
  { key: "variant_option_swatch", selector: "variant-selects input[data-option-value][data-size-option]", category: "size_picker" },
  { key: "variant_option_swatch", selector: "variant-selects input[data-option-value]:not([data-size-option])", category: "variant_picker" },
  { key: "size_finder_link", selector: '[data-deliberate-size-selector] a, [data-deliberate-size-selector] button', category: "size_picker" },

  // --- Matching-style / connected-product picker (snippets/product-variants.liquid) ---
  { key: "matching_style_option", selector: '.variant-picker a[is="magnet-link"]', category: "variant_picker" },

  // --- Complementary products carousel (snippets/product-complementary.liquid) ---
  { key: "complementary_product_card", selector: "product-complementary .product-card-horizontal, product-complementary [data-product-id]", category: "complementary_products" },
  { key: "complementary_prev", selector: "[data-prev-button]", category: "complementary_products" },
  { key: "complementary_next", selector: "[data-next-button]", category: "complementary_products" },

  // --- Hae Hae bundle widget (sections/hh-bundle-widget.liquid) ---
  { key: "bundle_add_to_cart", selector: "#hh-bundle-atc, [id^=hh-bundle-atc]", category: "bundle" },
  { key: "bundle_widget", selector: "#hh-bundle-widget", category: "bundle" },
  { key: "bundle_savings_amount", selector: "#hh-savings-amount", category: "bundle" },

  // --- Cart drawer / main cart (sections/main-cart.liquid) ---
  { key: "cart_checkout_button", selector: "[data-cart-main-checkout]", category: "checkout" },
  { key: "cart_line_item", selector: "cart-items [data-line-key]", category: "cart_drawer" },
  { key: "cart_quantity_control", selector: "main-cart quantity-input", category: "cart_drawer" },
  { key: "cart_note", selector: "cart-note", category: "cart_drawer" },

  // --- PDP accordion / shipping info (sections/product-details.liquid) ---
  { key: "product_accordion_toggle", selector: "accordions-details summary, accordions-details button[aria-expanded]", category: "accordion" },

  // --- Reviews (sections/main-reviews.liquid, snippets/marketplace-review-summary.liquid) ---
  { key: "review_summary", selector: ".marketplace-review-summary, [class*=review-summary]", category: "reviews" },
];

// Known section *containers* for section-visibility tracking (doc section
// 8), so the embed block works out of the box without requiring hand-edits
// to add data-hh-section="..." to every theme section. Also verified
// against the live theme files listed above. A merchant/dev can still add
// explicit data-hh-section attributes for anything not covered here (e.g.
// a custom section) — see extensions/hh-analytics/extensions/theme-embed.
export const KNOWN_SECTION_CONTAINERS: { key: string; selector: string }[] = [
  { key: "variant_selector", selector: "variant-selects" },
  { key: "bundle", selector: "#hh-bundle-widget" },
  { key: "reviews", selector: ".marketplace-review-summary, [id*=main-reviews], [class*=review-summary]" },
  { key: "product_accordion", selector: "accordions-details" },
  { key: "complementary_products", selector: "product-recommendations" },
  { key: "cart_drawer", selector: "main-cart, #cart" },
];

/**
 * Returns the KNOWN_SELECTORS entry whose selector matches the given
 * element (or one of its ancestors, up to `maxDepth` levels, so a click on
 * an icon *inside* the Add to Cart button still resolves to the button).
 */
export function matchKnownSelector(el: Element, maxDepth = 4): KnownSelector | null {
  let node: Element | null = el;
  let depth = 0;
  while (node && depth <= maxDepth) {
    for (const known of KNOWN_SELECTORS) {
      try {
        if (node.matches(known.selector)) return known;
      } catch {
        // Invalid selector for this browser — skip rather than throw.
      }
    }
    node = node.parentElement;
    depth++;
  }
  return null;
}
